import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, X } from "lucide-react";
import {
  getMasterMedicines, createMasterMedicine, updateMasterMedicine, deleteMasterMedicine,
} from "../../api/api";
import DataTable from "../../components/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { MEDICINE_FORM_LABEL } from "../../lib/prescription-constants";

const FORMS = Object.keys(MEDICINE_FORM_LABEL);
const EMPTY = {
  name: "", generic_name: "", form: "tablet", strength: "",
  category: "General", default_dosage: "", default_instructions: "", active: true,
};

function MedicineForm({ open, onOpenChange, medicine, onSaved, categories }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!medicine;

  useEffect(() => {
    if (!open) return;
    setForm(medicine ? { ...EMPTY, ...medicine } : EMPTY);
  }, [open, medicine]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        generic_name: form.generic_name.trim(),
        form: form.form,
        strength: form.strength.trim(),
        category: form.category.trim() || "General",
        default_dosage: form.default_dosage.trim(),
        default_instructions: form.default_instructions.trim(),
        active: !!form.active,
      };
      if (editing) await updateMasterMedicine(medicine._id, payload);
      else await createMasterMedicine(payload);
      toast.success(editing ? "Medicine updated." : "Medicine added to the catalogue.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save the medicine.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit medicine" : "Add medicine"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="m-name">Medicine name *</Label>
            <Input id="m-name" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Paracetamol 500mg Tablet" required />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="m-generic">Generic name</Label>
              <Input id="m-generic" value={form.generic_name} onChange={(e) => set("generic_name", e.target.value)}
                placeholder="Paracetamol" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-cat">Category</Label>
              <Input id="m-cat" list="medicine-categories" value={form.category}
                onChange={(e) => set("category", e.target.value)} placeholder="Analgesic" />
              <datalist id="medicine-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="m-form">Form</Label>
              <select id="m-form" value={form.form} onChange={(e) => set("form", e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                {FORMS.map((f) => <option key={f} value={f}>{MEDICINE_FORM_LABEL[f]}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-strength">Strength</Label>
              <Input id="m-strength" value={form.strength} onChange={(e) => set("strength", e.target.value)}
                placeholder="500mg" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="m-dosage">Default dosage</Label>
            <Input id="m-dosage" value={form.default_dosage} onChange={(e) => set("default_dosage", e.target.value)}
              placeholder="1-0-1" />
            <p className="text-xs text-muted-foreground">Pre-fills when this medicine is added to a prescription — editable per patient.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="m-instructions">Default instructions</Label>
            <Input id="m-instructions" value={form.default_instructions}
              onChange={(e) => set("default_instructions", e.target.value)} placeholder="After food" />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v)} />
            Available for prescribing
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add medicine"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MedicineMaster() {
  const [medicines, setMedicines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMasterMedicines({ search: search || undefined, page, limit: 20 })
      .then((res) => {
        setMedicines(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
        if (res.categories) setCategories(res.categories);
      })
      .catch((err) => toast.error(err?.message || "Could not load the medicine catalogue."))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (medicine) => {
    try {
      const res = await deleteMasterMedicine(medicine._id);
      toast.success(res.message || "Medicine removed.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not remove the medicine.");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Medicine",
      render: (m) => (
        <div className="min-w-0">
          <p className="truncate font-medium leading-tight">{m.name}</p>
          <p className="truncate text-xs leading-tight text-muted-foreground">
            {m.generic_name || "—"} · {m.category}
          </p>
        </div>
      ),
    },
    {
      key: "form",
      header: "Form",
      render: (m) => (
        <span className="text-sm text-muted-foreground">
          {MEDICINE_FORM_LABEL[m.form] || m.form}{m.strength ? ` · ${m.strength}` : ""}
        </span>
      ),
    },
    {
      key: "default_dosage",
      header: "Default dosing",
      render: (m) => (
        <div className="text-sm">
          <p className="tabular-nums">{m.default_dosage || "—"}</p>
          <p className="text-xs text-muted-foreground">{m.default_instructions || ""}</p>
        </div>
      ),
    },
    {
      key: "active",
      header: "Status",
      render: (m) => (
        <Badge variant={m.active ? "secondary" : "outline"}>
          {m.active ? "Active" : "Retired"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (m) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8"
            aria-label={`Edit ${m.name}`}
            onClick={() => { setEditing(m); setFormOpen(true); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${m.name}`}
            onClick={() => remove(m)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Medicines</h2>
          <p className="text-sm text-muted-foreground">
            Every medicine available to prescribe, with default dosing
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4" /> Add medicine
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, generic name, or category…"
          className="h-9 pl-9 pr-9"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {!loading && (
        <p className="text-sm text-muted-foreground">
          {meta.total} medicine{meta.total === 1 ? "" : "s"}
        </p>
      )}

      <DataTable
        columns={columns}
        data={medicines}
        loading={loading}
        emptyMessage={search ? "No medicines match that search." : "The catalogue is empty."}
        emptyAction={
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" /> Add the first medicine
          </Button>
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(m) => m._id}
      />

      <MedicineForm
        open={formOpen}
        onOpenChange={setFormOpen}
        medicine={editing}
        categories={categories}
        onSaved={load}
      />
    </div>
  );
}
